import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Snackbar,
  Button,
} from '@mui/material';
import { Store, Refresh,RestartAlt } from '@mui/icons-material';
import TokenTypeForm from './TokenTypeTab';
import { useRestaurant } from "./../contexts/RestaurantContext";
import { createTokenSettings, updateTokenSettings, getTokenSettingsByOutlet, resetTokenSettings } from '../services/tokenService';
import { useNotification } from './../contexts/NotificationContext'
import outletService from '../services/outletService';


// Tab configuration - matched to backend field names
const TAB_CONFIG = [
  { id: 'token', label: 'Token', icon: '🥡', color: 'primary', backendKey: 'token_prefix' },
  { id: 'catering', label: 'Catering', icon: '🎯', color: 'primary', backendKey: 'catering_prefix' },
  { id: 'reservation', label: 'Table Reservation', icon: '🪑', color: 'primary', backendKey: 'reservation_prefix' },
  { id: 'dine_in', label: 'Dine In', icon: '🍽️', color: 'primary', backendKey: 'dineIn_prefix' },
  { id: 'invoice', label: 'Invoice', icon: '🧾', color: 'primary', backendKey: 'invoice_prefix' },
  { id: 'kitchen', label: 'Kitchen', icon: '👨‍🍳', color: 'primary', backendKey: 'kitchen_prefix' },
];

// Default settings structure matching backend schema
const DEFAULT_SETTINGS = {
  token_prefix: { prefix: 'TAK', start_number: 1, numbers: 3 },
  catering_prefix: { prefix: 'CAT', start_number: 1, numbers: 3 },
  reservation_prefix: { prefix: 'TR', start_number: 1, numbers: 4 },
  dineIn_prefix: { prefix: 'DI', start_number: 1, numbers: 3 },
  invoice_prefix: { prefix: 'INV', start_number: 1, numbers: 5 },
  kitchen_prefix: { prefix: 'KIT', start_number: 1, numbers: 3 },
};

// Initialize empty token settings
const INITIAL_TOKEN_SETTINGS = {
  token_prefix: null,
  catering_prefix: null,
  reservation_prefix: null,
  dineIn_prefix: null,
  invoice_prefix: null,
  kitchen_prefix: null,
};

const TokenManagement = () => {
  const { outlets } = useRestaurant();
  const { showSuccess, showError } = useNotification()
  const [adminOutlets,setAdminOutlets] = useState([])
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [tokenSettings, setTokenSettings] = useState(INITIAL_TOKEN_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [hasSettings, setHasSettings] = useState(false);
  const [resetOpen, setResetOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState()
  const [resetOption,setResetOption] = useState("")
  const [reload,setReload] = useState(false)

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

     useEffect( () => {
        async function fetchAdminOutlets (){
          try{
            const response = await outletService.getOutlets()
            setAdminOutlets(response)
          }
          catch(error){
            console.error(error)
          }
        }
        fetchAdminOutlets()
    
      },[])

  // Load token settings when outlet changes
  useEffect(() => {
    const loadTokenSettings = async () => {
      if (!selectedOutlet) {
        setTokenSettings(INITIAL_TOKEN_SETTINGS);
        setHasSettings(false);
        return;
      }

      setFetching(true);
      try {
        const response = await getTokenSettingsByOutlet(selectedOutlet);

        // Merge fetched settings with defaults for missing values
        const mergedSettings = { ...DEFAULT_SETTINGS };
        Object.keys(mergedSettings).forEach(key => {
          mergedSettings[key] = response[key] || mergedSettings[key];
        });
        // console.log(response)
        // console.log("Merged Settings => ", mergedSettings)
        setTokenSettings(mergedSettings);
        setResetOption(response.reset_for)
        setHasSettings(true);
        // showSnackbar('Token settings loaded successfully', 'success');
      } catch (error) {
        console.error('Error loading token settings:', error);
        setTokenSettings(INITIAL_TOKEN_SETTINGS);
        setHasSettings(false);
        // showSnackbar('No settings found. Please create new settings.', 'info');
      } finally {
        setFetching(false);
      }
    };

    loadTokenSettings();
  }, [selectedOutlet,reload]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value);
    setResetOpen(false)
    setSelectedOption("")
    setActiveTab(0);
  };

  const getCurrentTabSettings = () => {
    const currentTab = TAB_CONFIG[activeTab];
    const backendKey = currentTab.backendKey;

    // Return the settings for current tab or null if not configured
    return tokenSettings[backendKey] || null;
  };

  const handleSaveSettings = async (type, formData) => {
    if (!selectedOutlet) {
      // showSnackbar('Please select an outlet first', 'error');
      return;
    }

    setLoading(true);
    try {
      const currentTab = TAB_CONFIG.find(tab => tab.id === type);
      const backendKey = currentTab.backendKey;

      // Prepare data for backend
      const requestData = {
        [backendKey]: formData,
      };

      let response;
      if (hasSettings) {
        // Update existing settings
        response = await updateTokenSettings(selectedOutlet, requestData);

        // Update only the changed field in local state
        setTokenSettings(prev => ({
          ...prev,
          [backendKey]: formData,
        }));
      } else {
        // Create new settings
        requestData.outlet_id = parseInt(selectedOutlet);
        response = await createTokenSettings(requestData);

        // Initialize all settings with the created one
        const newSettings = { ...INITIAL_TOKEN_SETTINGS };
        newSettings[backendKey] = formData;
        setTokenSettings(newSettings);
        setHasSettings(true);
      }

      showSuccess(`${currentTab.label} settings saved successfully!`);
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshOutlets = () => {
    // You can implement refresh logic here
    showSnackbar('Refreshing outlets...', 'info');
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleResetSave = async () => {
    // console.log("Option Selected is  => ", selectedOption)

    if (selectedOutlet === "") {
      showError("Please Select Outlet");
      return;
    }

    let data = {
      "outlet_id": parseInt(selectedOutlet),
      "time": selectedOption
    }

    try {
      const response = await resetTokenSettings(data)
      showSuccess("Token Resetted Successfully")
      setReload(!reload)
    } catch (error) {
      showError(error?.message)
    }

  }

  const selectedOutletData = outlets.find(outlet => outlet.id === parseInt(selectedOutlet));
  const currentTab = TAB_CONFIG[activeTab];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            fontWeight="bold"
            gutterBottom
            sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }}
          >
            Token Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure token prefix and numbering for different service types
          </Typography>
        </Box>
        {selectedOutlet !== "" &&
          <Box>
            {!resetOpen ?
              <Button variant='contained'
              sx={{mt: 1}}
                onClick={() => setResetOpen(true)}
                startIcon={<RestartAlt/>}

              >
                Reset Option</Button> :
              <>
                <Box>
                  <FormControl size="small" sx={{ minWidth: 150, mr: 2, mt: 1 }}>
                    <InputLabel id="demo-select-label">Select</InputLabel>
                    <Select
                      labelId="demo-select-label"
                      id="demo-select"
                      value={selectedOption}
                      label="Select"
                      onChange={(e) => setSelectedOption(e.target.value)}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant='contained' sx={{ mr: 1, color: "white" }} color="success" onClick={handleResetSave}>Save</Button>
                  <Button variant='contained' color="error" onClick={() => setResetOpen(false)}>Cancel</Button>
                </Box>
                <Box sx={{ ml: 1, mt: 1 }}>Current Reset Option : {resetOption.toUpperCase()}</Box>

              </>

            }
          </Box>
        }




      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Outlet Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: '6px', boxShadow: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center' }}>
          <Box sx={{ flex: 1, width: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Store />
              Select Outlet
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Choose Outlet</InputLabel>
              <Select
                value={selectedOutlet}
                onChange={handleOutletChange}
                label="Choose Outlet"
                disabled={fetching}
              >
                <MenuItem value="">
                  <em>Select an outlet</em>
                </MenuItem>
                {adminOutlets.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.id}>
                    {outlet.name} - {outlet.address}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* {selectedOutletData && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected:
              </Typography>
              <Chip
                label={selectedOutletData.name}
                color="primary"
                variant="outlined"
                icon={<Store />}
              />
            </Box>  
          )} */}

          {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Need to refresh outlets?
            </Typography>
            <button
              onClick={handleRefreshOutlets}
              disabled={fetching}
              style={{
                padding: '8px 16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: 'white',
                cursor: fetching ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              <Refresh />
              Refresh
            </button>
          </Box> */}
        </Box>
      </Paper>

      {/* Main Content */}
      {selectedOutlet ? (
        <Paper sx={{ borderRadius: '6px', boxShadow: 1, overflow: 'hidden' }}>
          {fetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tabs Header */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  {TAB_CONFIG.map((tab, index) => (
                    <Tab
                      key={tab.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{tab.icon}</span>
                          <span>{isMobile ? tab.label.split(' ')[0] : tab.label}</span>
                        </Box>
                      }
                      sx={{ textTransform: 'none', minHeight: 60 }}
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ p: { xs: 2, md: 4 } }}>
                <TokenTypeForm
                  type={currentTab.id}
                  label={currentTab.label}
                  icon={currentTab.icon}
                  color={currentTab.color}
                  hasSettings={hasSettings}
                  currentSettings={getCurrentTabSettings()}
                  defaultSettings={hasSettings ? tokenSettings[currentTab.backendKey] : DEFAULT_SETTINGS[currentTab.backendKey]}
                  onSave={handleSaveSettings}
                  loading={loading}
                />
              </Box>
            </>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '6px', boxShadow: 1 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Please select an outlet to configure token settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose an outlet from the dropdown above to begin configuration
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TokenManagement;